import { Autocomplete, Divider, Grid, IconButton, InputAdornment, TextField, Typography } from '@mui/material'
import { useContext, useEffect, useState } from 'react'
import globalStyles from '../../../../Styles/ComponentStyles/global.module.scss';
import styles from '../../../../Styles/ComponentStyles/ProfileStyle/ProfileNewAnnuity.module.scss'
import GlobalNavbar from '../../../../GlobalComponent/GlobalNavbar/GlobalNavbar';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Astrict from '../../../../GlobalComponent/Astrict/Astrict';
import DeleteIcon from '@mui/icons-material/Delete';
import GlobalButton from '../../../../Utils/GlobalUtilsComponents/GlobalButton';
import IconsColor from '../../../../Utils/IconsColor';
import { UserContext, UserContextPayload } from '../../../../Context/UserDetailContext';
import { useSnackbar } from '../../../../GlobalComponent/GlobalSnackbar/GlobalSnackbar';
import agent from '../../../../service/api';
import { ProfileContext, ProfileContextPayload } from '../../../../Context/ProfileDetailsContext';
import { formatWithThousandSeparator } from '../../../../Utils/HelperFunctions';

const NAME_REGEX = /^[A-Za-z0-9 ]{1,30}$/;
const CONTACT_REGEX = /^\d{10}$/;
const EMAIL_REGEX = /^([a-zA-Z0-9._]+)@([a-zA-Z0-9-]+)\.([a-zA-Z]{2,})(\.[a-zA-Z]{2,})?$/;

const CURRENCY_FIELDS = new Set([
    'incomeClientDetailSalary',
    'incomeSourceClientDetailAmount',
    'desiredAnnualIncomeAmount'
]);
const PERCENTAGE_FIELDS = new Set([
    'incomeSourceClientDetailAnnualIncrement',
    'desiredAnnualIncomeAnnualIncrement'
]);

const formatCurrencyInput = (value: string) => {
    const stripped = value.replaceAll(',', '');
    if (!Number.isNaN(Number(stripped)) && stripped.trim() !== '') {
        return Number(stripped).toLocaleString();
    }
    return value;
};

const isPercentageInRange = (value: any) => {
    const num = Number(value);
    return num >= 0 && num <= 100;
};

const removeDollarAndComma = (str: any) =>
    Number.parseFloat(str.toString().replaceAll(/[^\d.-]+/g, ''));

const allowOnlyDigits = (e: React.KeyboardEvent) => {
    if (!/\d$/.test(e.key)) {
        e.preventDefault();
    }
};

const allowDigitsAndDot = (e: React.KeyboardEvent) => {
    const value = (e.target as HTMLInputElement).value;
    if (!/^[\d.]$/.test(e.key) || (e.key === '.' && value.includes('.'))) {
        e.preventDefault();
    }
};

const formatIncomeSourceFromApi = (source: any) => ({
    ...source,
    incomeSourceClientDetailAmount: formatWithThousandSeparator(source?.incomeSourceClientDetailAmount)
});

const formatIncomeClientFromApi = (item: any) => ({
    ...item,
    incomeClientDetailSalary: formatWithThousandSeparator(item?.incomeClientDetailSalary),
    incomeSources: item?.incomeSources?.map(formatIncomeSourceFromApi)
});

const updateIncomeSourceAtIndex = (
    sources: any[],
    sourceIndex: number,
    field: string,
    value: any
) =>
    sources.map((source, sIndex) =>
        sIndex === sourceIndex ? { ...source, [field]: value } : source
    );

const updateClientAtIndex = (
    clients: any[],
    clientIndex: number,
    field: string,
    value: any,
    incomeSourceIndex?: number,
    incomeSourceField?: string
) =>
    clients.map((client, cIndex) => {
        if (cIndex !== clientIndex) {
            return client;
        }
        if (incomeSourceIndex !== undefined && incomeSourceField) {
            return {
                ...client,
                incomeSources: updateIncomeSourceAtIndex(
                    client.incomeSources,
                    incomeSourceIndex,
                    incomeSourceField,
                    value
                )
            };
        }
        return { ...client, [field]: value };
    });

const clearUniversalInputError = (errors: any, field: string, value: any) => {
    const universal = errors.universalInput;
    if (!universal) {
        return;
    }
    if (field === 'firstName' && NAME_REGEX.test(String(value).trim())) {
        delete universal.firstName;
    }
    if (field === 'currentAge' && value >= 0 && value <= 120) {
        delete universal.currentAge;
    }
    if (field === 'ageAtRetirement' && value >= 0 && value <= 120) {
        delete universal.ageAtRetirement;
    }
    if (field === 'Contact' && CONTACT_REGEX.test(value)) {
        delete universal.Contact;
    }
    if (field === 'email' && EMAIL_REGEX.test(value)) {
        delete universal.email;
    }
    if (field === 'userIncomePlanYears') {
        delete universal.userIncomePlanYears;
    }
};

const clearFieldFormErrors = (prevErrors: any, section: string | undefined, field: string, value: any) => {
    const updatedErrors = { ...prevErrors };
    if (section === 'universalInput') {
        clearUniversalInputError(updatedErrors, field, value);
    }
    if (field === 'desiredAnnualIncomeAmount') {
        delete updatedErrors.desiredAnnualIncomeAmount;
    }
    if (field === 'desiredAnnualIncomeAnnualIncrement') {
        delete updatedErrors.desiredAnnualIncomeAnnualIncrement;
    }
    if (field === 'desiredAnnualIncomeYear') {
        delete updatedErrors.desiredAnnualIncomeYear;
    }
    return updatedErrors;
};

const clearClientFormErrors = (
    prev: any,
    clientIndex: number,
    field: any,
    incomeSourceIndex?: number,
    incomeSourceField?: string
) => {
    const newErrors = { ...prev };
    const clientErrorsList = newErrors.incomeClientData;
    if (!clientErrorsList?.[clientIndex]) {
        return newErrors;
    }

    const clientErrors = { ...clientErrorsList[clientIndex] };

    if (incomeSourceIndex !== undefined && incomeSourceField) {
        const sourceErrors = clientErrors.incomeSources?.[incomeSourceIndex];
        if (sourceErrors?.[incomeSourceField]) {
            delete sourceErrors[incomeSourceField];
            if (Object.keys(sourceErrors).length === 0) {
                clientErrors.incomeSources.splice(incomeSourceIndex, 1);
            }
        }
    } else if (clientErrors[field]) {
        delete clientErrors[field];
    }

    if (Object.keys(clientErrors).length === 0) {
        clientErrorsList.splice(clientIndex, 1);
    } else {
        clientErrorsList[clientIndex] = clientErrors;
    }

    if (clientErrorsList.length === 0) {
        delete newErrors.incomeClientData;
    }

    return newErrors;
};

const validateUniversalInputIncome = (universalInput: any, requireContactAndEmail: boolean) => {
    const errors: any = {};
    let isValid = true;

    if (!universalInput?.firstName?.trim()) {
        errors.firstName = 'First name is required';
        isValid = false;
    } else if (!NAME_REGEX.test(universalInput.firstName)) {
        errors.firstName = 'Please enter a valid firstname';
        isValid = false;
    }

    if (!universalInput?.currentAge) {
        errors.currentAge = 'Current age is required';
        isValid = false;
    } else if (universalInput.currentAge < 0 || universalInput.currentAge > 120) {
        errors.currentAge = 'Please enter a valid age between 0 and 120';
        isValid = false;
    }

    if (!universalInput?.ageAtRetirement) {
        errors.ageAtRetirement = 'Retirement age is required';
        isValid = false;
    } else if (universalInput.ageAtRetirement <= universalInput.currentAge) {
        errors.ageAtRetirement = 'Retirement age must be greater than current age';
        isValid = false;
    }

    if (requireContactAndEmail) {
        if (!universalInput?.Contact?.trim()) {
            errors.Contact = 'Contact number is required';
            isValid = false;
        } else if (!CONTACT_REGEX.test(universalInput.Contact)) {
            errors.Contact = 'Please enter a valid 10-digit contact number';
            isValid = false;
        }
        if (!universalInput?.email?.trim()) {
            errors.email = 'Email is required';
            isValid = false;
        } else if (!EMAIL_REGEX.test(universalInput.email)) {
            errors.email = 'Please enter a valid email address';
            isValid = false;
        }
    }

    if (!String(universalInput?.userIncomePlanYears ?? '').trim()) {
        errors.userIncomePlanYears = 'Income plan years are required';
        isValid = false;
    }

    return { errors, isValid };
};

const validateDesiredIncomeFields = (data: any) => {
    const errors: any = {};
    let isValid = true;

    if (!data?.desiredAnnualIncomeAmount) {
        errors.desiredAnnualIncomeAmount = 'Desired Annual Income Amount is required';
        isValid = false;
    }
    if (!data?.desiredAnnualIncomeAnnualIncrement) {
        errors.desiredAnnualIncomeAnnualIncrement = 'Desired Annual Increment is required';
        isValid = false;
    }

    return { errors, isValid };
};

const validateIncomeSource = (source: any) => {
    const sourceErrors: any = {};
    let isValid = true;

    if (!source.incomeSourceClientDetailName?.trim()) {
        sourceErrors.incomeSourceClientDetailName = 'Income source name is required';
        isValid = false;
    }
    if (source?.incomeSourceClientDetailAgeOption === 'Enter Age Manually' && !String(source.incomeSourceClientDetailAge ?? '').trim()) {
        sourceErrors.incomeSourceClientDetailAge = 'Income source age is required';
        isValid = false;
    }
    if (source?.incomeSourceClientDetailYearOption === 'Enter Years Manually' && !String(source.incomeSourceClientDetailYear ?? '').trim()) {
        sourceErrors.incomeSourceClientDetailYear = 'Income source year is required';
        isValid = false;
    }
    if (!String(source.incomeSourceClientDetailAnnualIncrement ?? '').trim()) {
        sourceErrors.incomeSourceClientDetailAnnualIncrement = 'Income source annual increment is required';
        isValid = false;
    }
    if (!String(source.incomeSourceClientDetailAmount ?? '').trim()) {
        sourceErrors.incomeSourceClientDetailAmount = 'Income source amount is required';
        isValid = false;
    }
    if (!String(source.incomeSourceClientDetailAgeOption ?? '').trim()) {
        sourceErrors.incomeSourceClientDetailAgeOption = 'Please select a start age option';
        isValid = false;
    }
    if (!String(source.incomeSourceClientDetailYearOption ?? '').trim()) {
        sourceErrors.incomeSourceClientDetailYearOption = 'Please select an income duration option';
        isValid = false;
    }

    return { sourceErrors, isValid };
};

const validateIncomeClient = (client: any) => {
    const clientErrors: any = {};
    let isValid = true;

    if (!client.incomeClientDetailName?.trim()) {
        clientErrors.incomeClientDetailName = "Client's name is required";
        isValid = false;
    }
    if (!String(client.incomeClientDetailAge ?? '').trim()) {
        clientErrors.incomeClientDetailAge = "Client's age is required";
        isValid = false;
    }
    if (!String(client.incomeClientDetailSalary ?? '').trim()) {
        clientErrors.incomeClientDetailSalary = "Client's salary is required";
        isValid = false;
    }
    if (!String(client.incomeClientDetailRetirementAge ?? '').trim()) {
        clientErrors.incomeClientDetailRetirementAge = "Client's retirement age is required";
        isValid = false;
    }

    if (client.incomeSources?.length) {
        clientErrors.incomeSources = [];
        client.incomeSources.forEach((source: any, sIndex: number) => {
            const { sourceErrors, isValid: sourceValid } = validateIncomeSource(source);
            isValid = isValid && sourceValid;
            if (Object.keys(sourceErrors).length > 0) {
                clientErrors.incomeSources[sIndex] = sourceErrors;
            }
        });
        if (clientErrors.incomeSources.length === 0) {
            delete clientErrors.incomeSources;
        }
    } else {
        clientErrors.incomeSources = 'At least one income source is required';
        isValid = false;
    }

    return { clientErrors, isValid };
};

const validateIncomeClients = (incomeClientData: any[]) => {
    const errors: any[] = [];
    let isValid = true;

    incomeClientData.forEach((client: any, cIndex: number) => {
        const { clientErrors, isValid: clientValid } = validateIncomeClient(client);
        isValid = isValid && clientValid;
        if (Object.keys(clientErrors).length > 0) {
            errors[cIndex] = clientErrors;
        }
    });

    return { errors: errors.length > 0 ? errors : undefined, isValid };
};

const validateProfileForm = (finalIncomeData: any, flagSaveAsSample: boolean) => {
    const errors: any = {};
    let isValid = true;
    const requireContactAndEmail = finalIncomeData?.sample_profile ? flagSaveAsSample : true;

    if (finalIncomeData?.universalInput) {
        const universal = validateUniversalInputIncome(finalIncomeData.universalInput, requireContactAndEmail);
        errors.universalInput = universal.errors;
        isValid = isValid && universal.isValid;
    } else {
        errors.universalInput = {};
        isValid = false;
    }

    const desired = validateDesiredIncomeFields(finalIncomeData);
    Object.assign(errors, desired.errors);
    isValid = isValid && desired.isValid;

    if (finalIncomeData?.incomeClientData?.length) {
        const clients = validateIncomeClients(finalIncomeData.incomeClientData);
        if (clients.errors) {
            errors.incomeClientData = clients.errors;
        }
        isValid = isValid && clients.isValid;
    } else {
        errors.incomeClientData = { message: 'At least one client is required' };
        isValid = false;
    }

    return { isValid, errors };
};

const ProfileNewAnnuityIncome = () => {
    const {
        userDetail,
        setFlagLoader
    } = useContext(UserContext) as UserContextPayload

    const {
        updateUserProfileAPI,
        createNewUserProfile,
        getparticularUserProfileDetail
    } = useContext(ProfileContext) as ProfileContextPayload

    const userRole = userDetail?.user_data?.user_role_type?.toLowerCase();
    const { showSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const userId = searchParams.get('user_id');
    const isEditMode = Boolean(userId);

    const [agentList, setAgentList] = useState<any>([])
    const [finalIncomeData, setFinalIncomeData] = useState({
        universalInput: {
            firstName: '',
            currentAge: '',
            ageAtRetirement: '',
            Contact: '',
            email: '',
            userIncomePlanYears: ''
        },
        incomeClientData: [
            {
                incomeClientDetailId: "",
                incomeClientDetailName: "",
                incomeClientDetailAge: "",
                incomeClientDetailSalary: "",
                incomeClientDetailRetirementAge: "",
                incomeSources: [
                    {
                        incomeSourceClientDetailId: "",
                        incomeSourceClientDetailName: "",
                        incomeSourceClientDetailAge: "",
                        incomeSourceClientDetailYear: "",
                        incomeSourceClientDetailAnnualIncrement: "",
                        incomeSourceClientDetailAmount: "",
                        incomeSourceClientDetailAgeOption: "",
                        incomeSourceClientDetailYearOption: ""
                    }
                ]
            }

        ],
        AssignProfile: {
            agentId: null,
            agentName: ''
        },
        desiredAnnualIncomeAmount: '',
        desiredAnnualIncomeAnnualIncrement: '',
        desiredAnnualIncomeYear: '',
        sample_profile: false
    })
    const [formErrors, setFormErrors] = useState<any>({});
    const [flagSampleProfile, setFlagSampleProfile] = useState(false);
    const [flagSaveAsSampleProfile, setFlagSaveAsSampleProfile] = useState(false);

    const startAgeOptions = [
        "Current Age",
        "Age at Retirement",
        "Enter Age Manually"
    ]

    const yearOptions = [
        "Until Retirement Begins",
        "Lifetime",
        "Enter Years Manually"
    ]

    const handleDeleteClientFields = (clientIndex: number) => {
        setFinalIncomeData((prev) => {
            const updatedClients = prev.incomeClientData.filter((_, index) => index !== clientIndex);

            return {
                ...prev,
                incomeClientData: updatedClients.length > 0 ? updatedClients : [
                    {
                        incomeClientDetailId: '',
                        incomeClientDetailName: '',
                        incomeClientDetailAge: '',
                        incomeClientDetailSalary: '',
                        incomeClientDetailRetirementAge: '',
                        incomeSources: [
                            {
                                incomeSourceClientDetailId: '',
                                incomeSourceClientDetailName: '',
                                incomeSourceClientDetailAge: '',
                                incomeSourceClientDetailYear: '',
                                incomeSourceClientDetailAnnualIncrement: '',
                                incomeSourceClientDetailAmount: '',
                                incomeSourceClientDetailAgeOption: "",
                                incomeSourceClientDetailYearOption: ""
                            }
                        ]
                    }
                ]
            };
        });
    };

    const handleAddClient = () => {
        setFinalIncomeData((prev) => ({
            ...prev,
            incomeClientData: [
                ...prev.incomeClientData,
                {
                    incomeClientDetailId: '',
                    incomeClientDetailName: '',
                    incomeClientDetailAge: '',
                    incomeClientDetailSalary: '',
                    incomeClientDetailRetirementAge: '',
                    incomeSources: [
                        {
                            incomeSourceClientDetailId: '',
                            incomeSourceClientDetailName: '',
                            incomeSourceClientDetailAge: '',
                            incomeSourceClientDetailYear: '',
                            incomeSourceClientDetailAnnualIncrement: '',
                            incomeSourceClientDetailAmount: '',
                            incomeSourceClientDetailAgeOption: "",
                            incomeSourceClientDetailYearOption: ""
                        }
                    ]
                }
            ]
        }));
    };

    const handleAddIncomeSourceAnnuity = (clientIndex: number) => {
        setFinalIncomeData((prev) => {
            const updatedClients = [...prev.incomeClientData];
            updatedClients[clientIndex] = {
                ...updatedClients[clientIndex],
                incomeSources: [
                    ...updatedClients[clientIndex].incomeSources,
                    {
                        incomeSourceClientDetailId: '',
                        incomeSourceClientDetailName: '',
                        incomeSourceClientDetailAge: '',
                        incomeSourceClientDetailYear: '',
                        incomeSourceClientDetailAnnualIncrement: '',
                        incomeSourceClientDetailAmount: '',
                        incomeSourceClientDetailAgeOption: "",
                        incomeSourceClientDetailYearOption: ""
                    }
                ]
            };

            return { ...prev, incomeClientData: updatedClients };
        });
    };

    const handleRemoveIncomeSourceAnnuity = (clientIndex: number, sourceIndex: number) => {
        setFinalIncomeData((prev) => {
            const updatedClients = [...prev.incomeClientData];
            const currentSources = updatedClients[clientIndex].incomeSources;

            const updatedSources =
                currentSources.length > 1
                    ? currentSources.filter((_, index) => index !== sourceIndex)
                    : [
                        {
                            incomeSourceClientDetailId: '',
                            incomeSourceClientDetailName: '',
                            incomeSourceClientDetailAge: '',
                            incomeSourceClientDetailYear: '',
                            incomeSourceClientDetailAnnualIncrement: '',
                            incomeSourceClientDetailAmount: '',
                            incomeSourceClientDetailAgeOption: "",
                            incomeSourceClientDetailYearOption: ""
                        }
                    ];

            updatedClients[clientIndex] = {
                ...updatedClients[clientIndex],
                incomeSources: updatedSources
            };

            return { ...prev, incomeClientData: updatedClients };
        });
    };

    const handleAgentChange = (event: React.SyntheticEvent, newValue: any) => {
        handleInputChange("agentId", newValue?.value, "AssignProfile");
        handleInputChange("agentName", newValue?.label, "AssignProfile");
    };

    const handleInputChange = (field: string, value: any, section?: string) => {
        let nextValue = value;
        if (CURRENCY_FIELDS.has(field)) {
            nextValue = formatCurrencyInput(value);
        }
        if (PERCENTAGE_FIELDS.has(field) && !isPercentageInRange(value)) {
            return;
        }
        setFinalIncomeData((prev: any) => {
            if (section) {
                return {
                    ...prev,
                    [section]: {
                        ...prev[section],
                        [field]: nextValue
                    }
                };
            }
            return {
                ...prev,
                [field]: nextValue
            };
        });
        setFormErrors((prevErrors: any) => clearFieldFormErrors(prevErrors, section, field, nextValue));
    };

    const handleClientDataChange = (
        clientIndex: number,
        field: any,
        value: any,
        incomeSourceIndex?: number,
        incomeSourceField?: string
    ) => {
        const activeField = field || incomeSourceField;
        let nextValue = value;
        if (CURRENCY_FIELDS.has(activeField)) {
            nextValue = formatCurrencyInput(value);
        }
        if (PERCENTAGE_FIELDS.has(activeField) && !isPercentageInRange(value)) {
            return;
        }
        setFinalIncomeData((prev) => ({
            ...prev,
            incomeClientData: updateClientAtIndex(
                prev.incomeClientData,
                clientIndex,
                field,
                nextValue,
                incomeSourceIndex,
                incomeSourceField
            )
        }));
        setFormErrors((prev: any) =>
            clearClientFormErrors(prev, clientIndex, field, incomeSourceIndex, incomeSourceField)
        );
    };

    const transformedDataForCreate = (finalIncomeData: any, flagSaveAsSample: boolean) => {
        const transformedData = {
            user_profile_type_id: 3,

            // universal input
            user_name: finalIncomeData?.universalInput?.firstName,
            user_current_age: finalIncomeData?.universalInput?.currentAge,
            user_retirement_age: finalIncomeData?.universalInput?.ageAtRetirement,
            user_contact: finalIncomeData?.universalInput?.Contact,
            user_email: finalIncomeData?.universalInput?.email,
            user_income_plan_years: finalIncomeData?.universalInput?.userIncomePlanYears,

            // income data
            user_income_client_data: finalIncomeData?.incomeClientData?.map((client: any) => ({
                client_name: client.incomeClientDetailName,
                client_current_age: client.incomeClientDetailAge,
                client_retirement_age: client.incomeClientDetailRetirementAge,
                // client_year: client.incomeClientDetailRetirementAge - client.incomeClientDetailAge,
                client_salary: removeDollarAndComma(client.incomeClientDetailSalary),
                client_income_source: client.incomeSources.map((source: any) => ({
                    income_source_name: source.incomeSourceClientDetailName,
                    income_source_age: source.incomeSourceClientDetailAge,
                    income_source_year: source.incomeSourceClientDetailYear,
                    income_source_annual_increment: source.incomeSourceClientDetailAnnualIncrement,
                    income_source_amount: removeDollarAndComma(source.incomeSourceClientDetailAmount),
                    income_source_age_option: source?.incomeSourceClientDetailAgeOption,
                    income_source_year_option: source?.incomeSourceClientDetailYearOption
                }))
            })),

            // desired income data
            user_desired_annual_income_amount: removeDollarAndComma(finalIncomeData?.desiredAnnualIncomeAmount),
            user_desired_annual_income_annual_increment: finalIncomeData?.desiredAnnualIncomeAnnualIncrement,
            user_desired_annual_income_year: finalIncomeData?.desiredAnnualIncomeYear,

            // assigned agent
            user_iul_assigned_agent_id: finalIncomeData?.AssignProfile?.agentId ? [finalIncomeData?.AssignProfile?.agentId] : [],

            sample_profile_flag: !flagSaveAsSample && Boolean(finalIncomeData?.sample_profile),
        }
        return transformedData
    }

    const buildApiPayload = (isUpdate: boolean) => {
        const payload: any = transformedDataForCreate(finalIncomeData, flagSaveAsSampleProfile);

        if (isUpdate) {
            payload.user_profile_id = userId;
        }

        if (finalIncomeData?.sample_profile && !flagSaveAsSampleProfile) {
            payload.user_email = '';
            payload.user_contact = '';
            payload.user_iul_assigned_agent_id = [];
        }

        return payload;
    };

    const handleProfileFormSubmit = async () => {
        try {
            const { isValid, errors } = validateProfileForm(finalIncomeData, flagSaveAsSampleProfile);

            if (!isValid) {
                setFormErrors(errors);
                showSnackbar("Please fill in all required fields correctly", "error");
                return;
            }

            setFlagLoader(true);

            const isUpdate = isEditMode && !flagSaveAsSampleProfile;
            const apiPayload = buildApiPayload(isUpdate);

            const response = isUpdate
                ? await updateUserProfileAPI(apiPayload)
                : await createNewUserProfile(apiPayload);

            if (response?.status) {
                const profileId = isUpdate ? userId : response?.data?.[0]?.user_profile_id;
                navigate(`/profile/annuityProfileDetails/annuityIncome?user_id=${profileId}`);
                showSnackbar(`Profile ${isUpdate ? 'Updated' : 'Created'} Successfully!`, "success");
            } else {
                showSnackbar(response?.error?.message || "Error creating profile", "error");
            }
        } catch (error) {
            console.log(error, "Error submitting data!");
        } finally {
            setFlagLoader(false);
            setFlagSaveAsSampleProfile(false);
        }
    }

    const handleCancel = () => {
        navigate(-1)
    }

    useEffect(() => {
        if (isEditMode) {
            const fetchParticularUserProfileDetail = async () => {
                try {
                    setFlagLoader(true);
                    const response = await getparticularUserProfileDetail(userId);

                    if (response?.status && response?.inputData?.[0]) {
                        const userData = response.inputData[0];
                        if (userData?.sample_profile) {
                            setFlagSampleProfile(true);
                        }
                        const emailValue = userData?.sample_profile ? '' : userData?.universalInput?.email;
                        const contactValue = userData?.sample_profile ? '' : userData?.universalInput?.Contact;
                        setFinalIncomeData({
                            ...userData,
                            universalInput: {
                                ...userData?.universalInput,
                                email: emailValue,
                                Contact: contactValue
                            },
                            incomeClientData: userData?.incomeClientData?.map(formatIncomeClientFromApi),
                            desiredAnnualIncomeAmount: formatWithThousandSeparator(userData?.desiredAnnualIncomeAmount)
                        });
                    }
                } catch (error) {
                    console.error("Error fetching user profile:", error);
                } finally {
                    setFlagLoader(false);
                }
            };

            fetchParticularUserProfileDetail();
        }
    }, [userId, isEditMode]);

    useEffect(() => {
        try {
            setFlagLoader(true)
            const getAgentListData = async () => {
                const response = await agent?.getAgentDetailsAPI?.get()
                if (response?.status) {
                    const agents = response?.data?.[0]?.agents;

                    if (!agents) {
                        setAgentList([]);
                        setFlagLoader(false);
                        return;
                    }

                    const filteredAgents = agents
                        .filter((agent: any) => agent?.user_status_name?.toLowerCase() === "active" && agent?.user_role_type?.toLowerCase() === "agent")
                        .map((agent: any) => ({
                            label: `${agent.user_firstname} ${agent.user_lastname}`,
                            value: agent.user_id
                        }));
                    setAgentList(filteredAgents);
                    setFlagLoader(false);
                } else {
                    showSnackbar(response?.error?.message || "Something went wrong!", "error")
                    setFlagLoader(false)
                }
            }
            if (userRole == "admin") {
                getAgentListData();
            } else {
                return
            }
        } catch (error) {
            console.log(error, "Error fetching data")
            setFlagLoader(false)
        } finally {
            setFlagLoader(false)
        }
    }, [])

    useEffect(() => {
        if (flagSaveAsSampleProfile) {
            handleProfileFormSubmit();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flagSaveAsSampleProfile]);

    return (
        <Grid container>
            <GlobalNavbar
                navbarBtnText={''}
                navbarText={isEditMode ? "Edit Profile" : "Create New Income Profile"}
                flagSaveCancelBtn
                flagHelperTooltip
                primaryBtnClick={handleProfileFormSubmit}
                secondaryBtnClick={handleCancel}
                saveAsSampleProfileClick={() => { setFlagSaveAsSampleProfile(true) }}
                iseEditMode={isEditMode}
                flagSampleProfile={flagSampleProfile}
                flagBorderBottom={true}
            />

            <Grid item xs={12} className={`${styles.newAnnuityMainItem}`}>

                <Grid container>
                    {/* UNIVERSAL INPUTS */}
                    <Grid item xs={12} className={`${globalStyles.globalPFBlurWrapper}`}>
                        <Grid container>
                            <Grid item xs={12} className={`${globalStyles.globalPFSectioHeadItem}`}>
                                <Typography
                                    component={'span'}
                                    className={`${globalStyles.globalPFlabel}`}
                                >
                                    Universal Inputs
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container sx={{ gap: "25px" }}>
                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    Name <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    required
                                                    variant="outlined"
                                                    placeholder='Enter Name'
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    className={`${globalStyles.globalPFTextfield}`}
                                                    onChange={(e) => handleInputChange('firstName', e.target.value, 'universalInput')}
                                                    value={finalIncomeData?.universalInput?.firstName}
                                                />
                                                <label className={globalStyles.helperTextStyle}>{formErrors?.universalInput?.firstName}</label>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    Age at Retirement <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    required
                                                    variant="outlined"
                                                    placeholder='Enter Age at Retirement'
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    className={`${globalStyles.globalPFTextfield}`}
                                                    onKeyDown={allowOnlyDigits}
                                                    onChange={(e) => handleInputChange('ageAtRetirement', e.target.value, 'universalInput')}
                                                    value={finalIncomeData?.universalInput?.ageAtRetirement}
                                                />
                                                <label className={globalStyles.helperTextStyle}>{formErrors?.universalInput?.ageAtRetirement}</label>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    Current Age <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    required
                                                    variant="outlined"
                                                    placeholder='Enter Currrent Age'
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    className={`${globalStyles.globalPFTextfield}`}
                                                    onKeyDown={allowOnlyDigits}
                                                    onChange={(e) => handleInputChange('currentAge', e.target.value, 'universalInput')}
                                                    value={finalIncomeData?.universalInput?.currentAge}
                                                />
                                                <label className={globalStyles.helperTextStyle}>{formErrors?.universalInput?.currentAge}</label>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    Cell Phone <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    required
                                                    variant="outlined"
                                                    placeholder={`${finalIncomeData?.sample_profile ? '' : 'Enter Cell Phone'}`}
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    className={`${globalStyles.globalPFTextfield}`}
                                                    onKeyDown={allowOnlyDigits}
                                                    onChange={(e) => handleInputChange('Contact', e.target.value, 'universalInput')}
                                                    value={finalIncomeData?.universalInput?.Contact}
                                                />
                                                <label className={globalStyles.helperTextStyle}>{formErrors?.universalInput?.Contact}</label>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    Email <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    required
                                                    variant="outlined"
                                                    placeholder={`${finalIncomeData?.sample_profile ? '' : 'Enter Email'}`}
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    className={`${globalStyles.globalPFTextfield}`}
                                                    onChange={(e) => handleInputChange('email', e.target.value, 'universalInput')}
                                                    value={finalIncomeData?.universalInput?.email}
                                                />
                                                <label className={globalStyles.helperTextStyle}>{formErrors?.universalInput?.email}</label>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    Income Plan Years <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    required
                                                    variant="outlined"
                                                    placeholder='Enter Income Plan Years'
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    className={`${globalStyles.globalPFTextfield}`}
                                                    onChange={(e) => handleInputChange('userIncomePlanYears', e.target.value, 'universalInput')}
                                                    value={finalIncomeData?.universalInput?.userIncomePlanYears}
                                                    onKeyDown={allowOnlyDigits}
                                                />
                                                <label className={globalStyles.helperTextStyle}>{formErrors?.universalInput?.userIncomePlanYears}</label>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* ANNUITY - INCOME */}
                    <Grid item xs={12} className={`${globalStyles.globalPFBlurWrapper}`}>
                        <Grid container>
                            <Grid item xs={12} className={`${globalStyles.globalPFSectioHeadItem}`}>
                                <Grid container justifyContent={"space-between"}>
                                    <Grid item>
                                        <Typography
                                            component={'span'}
                                            className={`${globalStyles.globalPFlabel}`}
                                        >
                                            Annuity - Income
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <GlobalButton
                                            value='New Client'
                                            size='medium'
                                            startIcon={<IconsColor iconName="plusIcon" width="13px" height="13px" />}
                                            className={`${globalStyles.incomeSourceButton}`}
                                            onClick={() => handleAddClient()}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>

                            {finalIncomeData?.incomeClientData?.length > 0 ? (
                                <>
                                    <Grid item xs={12}>
                                        <Typography component={"span"} className={globalStyles.globalPFlabel}>
                                            Client Details
                                        </Typography>
                                    </Grid>
                                    <Divider
                                        style={{
                                            opacity: 0.8,
                                            borderColor: "#D1D1D1",
                                            // backgroundColor: "black",
                                            height: "1px",
                                            width: "100%",
                                            marginTop: "20px"
                                        }}
                                    />
                                    {finalIncomeData?.incomeClientData?.map((field: any, clientIndex: any) => {
                                        return (
                                            <>
                                                <Grid item mt={"20px"}>
                                                    <Grid container>
                                                        <Grid item xs={12} sx={{ margin: "0px 0px 10px" }}>
                                                            <Grid container justifyContent={"space-between"}>
                                                                <Grid item>
                                                                    <Typography component={'span'} className={`${styles.clientDetailsTxt}`}>
                                                                        Client {clientIndex + 1}
                                                                    </Typography>
                                                                </Grid>
                                                                {finalIncomeData?.incomeClientData?.length >= 2 &&
                                                                    <Grid item className={`${globalStyles.globalIncomeRightSection}`}>
                                                                        <IconButton sx={{ padding: "2px", marginTop: "10px" }} onClick={() => handleDeleteClientFields(clientIndex)}>
                                                                            <DeleteIcon sx={{ color: "#314158" }} />
                                                                        </IconButton>
                                                                    </Grid>
                                                                }
                                                            </Grid>
                                                        </Grid>

                                                        <Grid item xs={12}>
                                                            <Grid container sx={{ gap: "25px" }}>
                                                                <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                                                    <Grid container>
                                                                        <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                                            <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                                                Client's Name <Astrict />
                                                                            </Typography>
                                                                        </Grid>
                                                                        <Grid item xs={12}>
                                                                            <TextField
                                                                                required
                                                                                variant="outlined"
                                                                                placeholder="Enter Name"
                                                                                size="medium"
                                                                                fullWidth
                                                                                autoComplete='off'
                                                                                className={`${globalStyles.globalPFTextfield}`}
                                                                                value={field.incomeClientDetailName}
                                                                                onChange={(e) => handleClientDataChange(clientIndex, 'incomeClientDetailName', e.target.value)}
                                                                            />
                                                                            <label className={globalStyles.helperTextStyle}>{formErrors?.incomeClientData?.[clientIndex]?.incomeClientDetailName}</label>
                                                                        </Grid>
                                                                    </Grid>
                                                                </Grid>

                                                                <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                                                    <Grid container>
                                                                        <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                                            <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                                                Current Age <Astrict />
                                                                            </Typography>
                                                                        </Grid>
                                                                        <Grid item xs={12}>
                                                                            <TextField
                                                                                required
                                                                                variant="outlined"
                                                                                placeholder='Enter Current Age'
                                                                                size="medium"
                                                                                fullWidth
                                                                                autoComplete='off'
                                                                                className={`${globalStyles.globalPFTextfield}`}
                                                                                onKeyDown={allowOnlyDigits}
                                                                                value={field.incomeClientDetailAge}
                                                                                onChange={(e) => handleClientDataChange(clientIndex, 'incomeClientDetailAge', e.target.value)}
                                                                            />
                                                                            <label className={globalStyles.helperTextStyle}>{formErrors?.incomeClientData?.[clientIndex]?.incomeClientDetailAge}</label>
                                                                        </Grid>
                                                                    </Grid>
                                                                </Grid>

                                                                <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                                                    <Grid container>
                                                                        <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                                            <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                                                Retirement Age <Astrict />
                                                                            </Typography>
                                                                        </Grid>
                                                                        <Grid item xs={12}>
                                                                            <TextField
                                                                                required
                                                                                variant="outlined"
                                                                                placeholder='Enter Retirement Age'
                                                                                size="medium"
                                                                                fullWidth
                                                                                autoComplete='off'
                                                                                className={`${globalStyles.globalPFTextfield}`}
                                                                                onKeyDown={allowOnlyDigits}
                                                                                value={field.incomeClientDetailRetirementAge}
                                                                                onChange={(e) => handleClientDataChange(clientIndex, 'incomeClientDetailRetirementAge', e.target.value)}
                                                                            />
                                                                            <label className={globalStyles.helperTextStyle}>{formErrors?.incomeClientData?.[clientIndex]?.incomeClientDetailRetirementAge}</label>
                                                                        </Grid>
                                                                    </Grid>
                                                                </Grid>

                                                                <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                                                    <Grid container>
                                                                        <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                                            <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                                                Salary <Astrict />
                                                                            </Typography>
                                                                        </Grid>
                                                                        <Grid item xs={12}>
                                                                            <TextField
                                                                                required
                                                                                variant="outlined"
                                                                                placeholder='Enter Salary'
                                                                                size="medium"
                                                                                fullWidth
                                                                                autoComplete='off'
                                                                                className={`${globalStyles.globalPFTextfield}`}
                                                                                onKeyDown={allowOnlyDigits}
                                                                                InputProps={{
                                                                                    startAdornment: (
                                                                                        <InputAdornment position="start">
                                                                                            <IconsColor iconName="dollarIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                                                        </InputAdornment>
                                                                                    ),
                                                                                }}
                                                                                value={field.incomeClientDetailSalary}
                                                                                onChange={(e) => handleClientDataChange(clientIndex, 'incomeClientDetailSalary', e.target.value)}
                                                                            />
                                                                            <label className={globalStyles.helperTextStyle}>{formErrors?.incomeClientData?.[clientIndex]?.incomeClientDetailSalary}</label>
                                                                        </Grid>
                                                                    </Grid>
                                                                </Grid>



                                                                <Grid item xs={12}>
                                                                    <Grid container gap="10px">
                                                                        {field?.incomeSources?.map((source: any, sourceIndex: any) => {
                                                                            return (
                                                                                <>
                                                                                    <Grid item xs={12}>
                                                                                        <Typography
                                                                                            component={'span'}
                                                                                            className={styles.retirementIncomeSourcesTxt}
                                                                                        >
                                                                                            Retirement Income Sources
                                                                                        </Typography>
                                                                                    </Grid>
                                                                                    <Grid item xs={12} sx={{ margin: "0px 0px 10px" }} className={`${globalStyles.globalIncomeWrapper}`}>
                                                                                        <Grid container sx={{ justifyContent: 'space-between', gap: "10px", alignItems: "center" }}>
                                                                                            <Grid item className={`${globalStyles.globalIncomeLeftSection}`}>
                                                                                                <Grid container sx={{ gap: "25px" }}>
                                                                                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                                                                                        <Grid container>
                                                                                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                                                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                                                                                    Retirement Income Source Name <Astrict />
                                                                                                                </Typography>
                                                                                                            </Grid>
                                                                                                            <Grid item xs={12}>
                                                                                                                <TextField
                                                                                                                    required
                                                                                                                    variant="outlined"
                                                                                                                    placeholder='Enter Income Source Name'
                                                                                                                    size="medium"
                                                                                                                    fullWidth
                                                                                                                    autoComplete='off'
                                                                                                                    className={`${globalStyles.globalPFTextfield}`}
                                                                                                                    value={source.incomeSourceClientDetailName}
                                                                                                                    onChange={(e) => handleClientDataChange(clientIndex, '', e.target.value, sourceIndex, 'incomeSourceClientDetailName')}
                                                                                                                />
                                                                                                                <label className={globalStyles.helperTextStyle}>{formErrors?.incomeClientData?.[clientIndex]?.incomeSources?.[sourceIndex]?.incomeSourceClientDetailName}</label>
                                                                                                            </Grid>
                                                                                                        </Grid>
                                                                                                    </Grid>

                                                                                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                                                                                        <Grid container>
                                                                                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                                                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                                                                                    Start Age Selection <Astrict />
                                                                                                                </Typography>
                                                                                                            </Grid>
                                                                                                            <Grid item xs={12}>
                                                                                                                <Autocomplete
                                                                                                                    disableClearable={true}
                                                                                                                    options={startAgeOptions}
                                                                                                                    className={globalStyles.globalAutoCompleteStyle}
                                                                                                                    sx={{
                                                                                                                        "& .MuiInputBase-root": {
                                                                                                                            padding: "8px !important",
                                                                                                                        },
                                                                                                                    }}
                                                                                                                    renderInput={(params) => (
                                                                                                                        <TextField
                                                                                                                            {...params}
                                                                                                                            size="small"
                                                                                                                            variant="outlined"
                                                                                                                            placeholder="Select Start Age"
                                                                                                                        />
                                                                                                                    )}
                                                                                                                    getOptionLabel={(option) => option}
                                                                                                                    value={source.incomeSourceClientDetailAgeOption}
                                                                                                                    onChange={(e, newValue) => {
                                                                                                                        handleClientDataChange(clientIndex, '', newValue, sourceIndex, 'incomeSourceClientDetailAgeOption')
                                                                                                                    }}
                                                                                                                />
                                                                                                                <label className={globalStyles.helperTextStyle}>{formErrors?.incomeClientData?.[clientIndex]?.incomeSources[sourceIndex]?.incomeSourceClientDetailAgeOption}</label>
                                                                                                            </Grid>
                                                                                                        </Grid>
                                                                                                    </Grid>

                                                                                                    {finalIncomeData?.incomeClientData[clientIndex]?.incomeSources[sourceIndex]?.incomeSourceClientDetailAgeOption === "Enter Age Manually" &&
                                                                                                        <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                                                                                            <Grid container>
                                                                                                                <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                                                                                    <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                                                                                        Manual Start Age Input <Astrict />
                                                                                                                    </Typography>
                                                                                                                </Grid>
                                                                                                                <Grid item xs={12}>
                                                                                                                    <TextField
                                                                                                                        required
                                                                                                                        variant="outlined"
                                                                                                                        placeholder='Enter Start Age'
                                                                                                                        size="medium"
                                                                                                                        fullWidth
                                                                                                                        autoComplete='off'
                                                                                                                        className={`${globalStyles.globalPFTextfield}`}
                                                                                                                        onKeyDown={allowOnlyDigits}
                                                                                                                        value={source.incomeSourceClientDetailAge}
                                                                                                                        onChange={(e) => handleClientDataChange(clientIndex, '', e.target.value, sourceIndex, 'incomeSourceClientDetailAge')}
                                                                                                                    />
                                                                                                                    <label className={globalStyles.helperTextStyle}>{formErrors?.incomeClientData?.[clientIndex]?.incomeSources[sourceIndex]?.incomeSourceClientDetailAge}</label>
                                                                                                                </Grid>
                                                                                                            </Grid>
                                                                                                        </Grid>
                                                                                                    }

                                                                                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                                                                                        <Grid container>
                                                                                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                                                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                                                                                    Income Duration Selection <Astrict />
                                                                                                                </Typography>
                                                                                                            </Grid>
                                                                                                            <Grid item xs={12}>
                                                                                                                <Autocomplete
                                                                                                                    disableClearable={true}
                                                                                                                    options={
                                                                                                                        source?.incomeSourceClientDetailAgeOption === "Age at Retirement"
                                                                                                                            ? yearOptions.filter(option => option !== "Until Retirement Begins")
                                                                                                                            : yearOptions
                                                                                                                    }
                                                                                                                    className={globalStyles.globalAutoCompleteStyle}
                                                                                                                    sx={{
                                                                                                                        "& .MuiInputBase-root": {
                                                                                                                            padding: "8px !important",
                                                                                                                        },
                                                                                                                    }}
                                                                                                                    renderInput={(params) => (
                                                                                                                        <TextField
                                                                                                                            {...params}
                                                                                                                            size="small"
                                                                                                                            variant="outlined"
                                                                                                                            placeholder="Select Duration"
                                                                                                                        />
                                                                                                                    )}
                                                                                                                    getOptionLabel={(option) => option}
                                                                                                                    value={source?.incomeSourceClientDetailAgeOption === "Age at Retirement" && source.incomeSourceClientDetailYearOption === "Until Retirement Begins" ? "" : source.incomeSourceClientDetailYearOption}
                                                                                                                    onChange={(e, newValue) => {
                                                                                                                        handleClientDataChange(clientIndex, '', newValue, sourceIndex, 'incomeSourceClientDetailYearOption')
                                                                                                                    }}
                                                                                                                />
                                                                                                                <label className={globalStyles.helperTextStyle}>{formErrors?.incomeClientData?.[clientIndex]?.incomeSources[sourceIndex]?.incomeSourceClientDetailYearOption}</label>
                                                                                                            </Grid>
                                                                                                        </Grid>
                                                                                                    </Grid>

                                                                                                    {finalIncomeData?.incomeClientData[clientIndex]?.incomeSources[sourceIndex]?.incomeSourceClientDetailYearOption === "Enter Years Manually" &&
                                                                                                        <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                                                                                            <Grid container>
                                                                                                                <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                                                                                    <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                                                                                        Manual Income Years Input <Astrict />
                                                                                                                    </Typography>
                                                                                                                </Grid>
                                                                                                                <Grid item xs={12}>
                                                                                                                    <TextField
                                                                                                                        required
                                                                                                                        variant="outlined"
                                                                                                                        placeholder='Enter Years'
                                                                                                                        size="medium"
                                                                                                                        fullWidth
                                                                                                                        autoComplete='off'
                                                                                                                        className={`${globalStyles.globalPFTextfield}`}
                                                                                                                        onKeyDown={allowOnlyDigits}
                                                                                                                        value={source.incomeSourceClientDetailYear}
                                                                                                                        onChange={(e) => handleClientDataChange(clientIndex, '', e.target.value, sourceIndex, 'incomeSourceClientDetailYear')}
                                                                                                                    />
                                                                                                                    <label className={globalStyles.helperTextStyle}>{formErrors?.incomeClientData?.[clientIndex]?.incomeSources?.[sourceIndex]?.incomeSourceClientDetailYear}</label>
                                                                                                                </Grid>
                                                                                                            </Grid>
                                                                                                        </Grid>
                                                                                                    }

                                                                                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                                                                                        <Grid container>
                                                                                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                                                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                                                                                    Annual Increment % <Astrict />
                                                                                                                </Typography>
                                                                                                            </Grid>
                                                                                                            <Grid item xs={12}>
                                                                                                                <TextField
                                                                                                                    required
                                                                                                                    variant="outlined"
                                                                                                                    placeholder=''
                                                                                                                    size="medium"
                                                                                                                    fullWidth
                                                                                                                    autoComplete='off'
                                                                                                                    className={`${globalStyles.globalPFTextfield}`}
                                                                                                                    value={source.incomeSourceClientDetailAnnualIncrement}
                                                                                                                    onChange={(e) => handleClientDataChange(clientIndex, '', e.target.value, sourceIndex, 'incomeSourceClientDetailAnnualIncrement')}
                                                                                                                    onKeyDown={allowDigitsAndDot}
                                                                                                                    InputProps={{
                                                                                                                        startAdornment: (
                                                                                                                            <InputAdornment position="start">
                                                                                                                                <IconsColor iconName="percentageIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                                                                                            </InputAdornment>
                                                                                                                        ),
                                                                                                                    }}
                                                                                                                />
                                                                                                                <label className={globalStyles.helperTextStyle}>{formErrors?.incomeClientData?.[clientIndex]?.incomeSources[sourceIndex]?.incomeSourceClientDetailAnnualIncrement}</label>
                                                                                                            </Grid>
                                                                                                        </Grid>
                                                                                                    </Grid>

                                                                                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                                                                                        <Grid container>
                                                                                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                                                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                                                                                    Amount <Astrict />
                                                                                                                </Typography>
                                                                                                            </Grid>
                                                                                                            <Grid item xs={12}>
                                                                                                                <TextField
                                                                                                                    required
                                                                                                                    variant="outlined"
                                                                                                                    placeholder='Enter Amount'
                                                                                                                    size="medium"
                                                                                                                    fullWidth
                                                                                                                    autoComplete='off'
                                                                                                                    className={`${globalStyles.globalPFTextfield}`}
                                                                                                                    onKeyDown={allowOnlyDigits}
                                                                                                                    InputProps={{
                                                                                                                        startAdornment: (
                                                                                                                            <InputAdornment position="start">
                                                                                                                                <IconsColor iconName="dollarIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                                                                                            </InputAdornment>
                                                                                                                        ),
                                                                                                                    }}
                                                                                                                    value={source.incomeSourceClientDetailAmount}
                                                                                                                    onChange={(e) => handleClientDataChange(clientIndex, '', e.target.value, sourceIndex, 'incomeSourceClientDetailAmount')}
                                                                                                                />
                                                                                                                <label className={globalStyles.helperTextStyle}>{formErrors?.incomeClientData?.[clientIndex]?.incomeSources[sourceIndex]?.incomeSourceClientDetailAmount}</label>
                                                                                                            </Grid>
                                                                                                        </Grid>
                                                                                                    </Grid>
                                                                                                </Grid>
                                                                                            </Grid>
                                                                                            {field?.incomeSources?.length >= 2 &&
                                                                                                <Grid item className={`${globalStyles.globalIncomeRightSection}`}>
                                                                                                    <IconButton sx={{ padding: "2px", marginTop: "10px" }} onClick={() => handleRemoveIncomeSourceAnnuity(clientIndex, sourceIndex)}>
                                                                                                        <DeleteIcon sx={{ color: "#314158" }} />
                                                                                                    </IconButton>
                                                                                                </Grid>
                                                                                            }
                                                                                        </Grid>
                                                                                    </Grid>
                                                                                </>
                                                                            )
                                                                        })}

                                                                        <Grid item xs={12} className={`${globalStyles.incomeSourceButtonItem}`}>
                                                                            <GlobalButton
                                                                                value='Add Retirement Income Source'
                                                                                size='medium'
                                                                                startIcon={<IconsColor iconName="plusIcon" width="13px" height="13px" />}
                                                                                className={`${globalStyles.incomeSourceButton}`}
                                                                                onClick={() => handleAddIncomeSourceAnnuity(clientIndex)}
                                                                            />
                                                                        </Grid>
                                                                    </Grid>
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                                {finalIncomeData?.incomeClientData?.length > 1 &&
                                                    <Divider
                                                        style={{
                                                            opacity: 0.8,
                                                            borderColor: "#D1D1D1",
                                                            // backgroundColor: "black",
                                                            height: "1px",
                                                            width: "100%",
                                                            marginTop: "30px"
                                                        }}
                                                    />
                                                }
                                            </>
                                        )
                                    })
                                    }
                                </>
                            ) :
                                (
                                    <span className={styles.clientDetailsTxt}>
                                        No Client Details Found
                                    </span>
                                )
                            }
                        </Grid>
                    </Grid >

                    {/* DESIRED ANNUAL INCOME */}
                    <Grid item xs={12} className={`${globalStyles.globalPFBlurWrapper}`}>
                        <Grid container>

                            <Grid item xs={12} sx={{ margin: "0px 0px 10px" }}>
                                <Typography component={'span'} className={`${globalStyles.globalPFlabel}`}>
                                    Desired Annual Income
                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Grid container sx={{ gap: "25px" }}>
                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    Annual Increment % <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    required
                                                    variant="outlined"
                                                    placeholder=''
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    className={`${globalStyles.globalPFTextfield}`}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <IconsColor iconName="percentageIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    onKeyDown={allowDigitsAndDot}
                                                    onChange={(e) => handleInputChange('desiredAnnualIncomeAnnualIncrement', e.target.value)}
                                                    value={finalIncomeData?.desiredAnnualIncomeAnnualIncrement}
                                                />
                                                <label className={globalStyles.helperTextStyle}>{formErrors?.desiredAnnualIncomeAnnualIncrement}</label>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    Amount <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    required
                                                    variant="outlined"
                                                    placeholder='Enter Amount'
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    className={`${globalStyles.globalPFTextfield}`}
                                                    onKeyDown={allowOnlyDigits}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <IconsColor iconName="dollarIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    onChange={(e) => handleInputChange('desiredAnnualIncomeAmount', e.target.value)}
                                                    value={finalIncomeData?.desiredAnnualIncomeAmount}
                                                />
                                                <label className={globalStyles.helperTextStyle}>{formErrors?.desiredAnnualIncomeAmount}</label>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* ASSIGN AGENT SECTION */}
                    {userRole != "agent" &&
                        <Grid item xs={12} className={`${globalStyles.globalPFBlurWrapper}`}>
                            <Grid container>
                                <Grid item xs={12} className={`${globalStyles.globalPFSectioHeadItem}`}>
                                    <Typography
                                        component={'span'}
                                        className={`${globalStyles.globalPFlabel}`}
                                    >
                                        Assign Profile
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} sm={12} md={9} lg={11} xl={4.5}>
                                    <Grid container>
                                        <Grid item className={`${globalStyles.globalPFLabel}`}>
                                            <Typography
                                                component='span'
                                                className={`${globalStyles.globalPFTextfieldLabel}`}
                                            >
                                                Agent Name
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Autocomplete
                                                options={agentList}
                                                className={globalStyles.globalAutoCompleteStyle}
                                                onChange={handleAgentChange}
                                                value={finalIncomeData?.AssignProfile?.agentName}
                                                renderInput={(params) => <TextField placeholder='Select Agent' {...params} />}
                                                sx={{
                                                    "& .MuiInputBase-root": {
                                                        padding: "3px !important"
                                                    }
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    }
                </Grid >
            </Grid >

        </Grid >
    )
}

export default ProfileNewAnnuityIncome